import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { cn } from '../lib/format';
import { errorMessage } from '../api/errors';

export const Spinner = ({ label = 'Loading' }: { label?: string }): JSX.Element => (
  <div className="flex items-center justify-center gap-2 py-8 text-ink-300" role="status">
    <span className="h-4 w-4 animate-spin rounded-full border-2 border-ink-500 border-t-brand-400" />
    <span className="text-sm">{label}</span>
  </div>
);

export const ErrorState = ({ error, onRetry }: { error: unknown; onRetry?: () => void }): JSX.Element => (
  <div className="rounded-lg border border-red-900/60 bg-red-950/30 p-4 text-sm text-red-200" role="alert">
    <p>{errorMessage(error)}</p>
    {onRetry !== undefined && (
      <button type="button" onClick={onRetry} className="mt-2 text-red-100 underline underline-offset-2">
        Try again
      </button>
    )}
  </div>
);

export const EmptyState = ({ title, hint }: { title: string; hint?: string }): JSX.Element => (
  <div className="rounded-lg border border-ink-600 bg-ink-800/50 p-8 text-center">
    <p className="text-ink-100">{title}</p>
    {hint !== undefined && <p className="mt-1 text-sm text-ink-300">{hint}</p>}
  </div>
);

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  children: ReactNode;
};

const VARIANTS: Record<NonNullable<ButtonProps['variant']>, string> = {
  primary: 'bg-brand-500 text-white hover:bg-brand-600 disabled:bg-ink-600',
  secondary: 'bg-ink-600 text-ink-100 hover:bg-ink-500 disabled:text-ink-400',
  ghost: 'bg-transparent text-ink-300 hover:bg-ink-700 hover:text-ink-100',
  danger: 'bg-red-700 text-white hover:bg-red-600',
};

export const Button = ({ variant = 'primary', className, children, ...rest }: ButtonProps): JSX.Element => (
  <button
    {...rest}
    className={cn(
      'inline-flex items-center justify-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-70',
      VARIANTS[variant],
      className,
    )}
  >
    {children}
  </button>
);

export const Avatar = ({
  src,
  name,
  size = 36,
}: {
  src: string | null;
  name: string;
  size?: number;
}): JSX.Element => {
  const initial = name.trim().charAt(0).toUpperCase() || '?';
  if (src === null || src === '') {
    return (
      <span
        className="inline-flex shrink-0 items-center justify-center rounded-full bg-ink-600 font-semibold text-ink-100"
        style={{ width: size, height: size, fontSize: size * 0.42 }}
        aria-hidden="true"
      >
        {initial}
      </span>
    );
  }
  return (
    <img
      src={src}
      alt=""
      width={size}
      height={size}
      className="shrink-0 rounded-full object-cover"
      style={{ width: size, height: size }}
    />
  );
};

/** A field wrapper so every form in the app labels and errors identically. */
export const Field = ({
  label,
  htmlFor,
  error,
  hint,
  children,
}: {
  label: string;
  htmlFor: string;
  error?: string | undefined;
  hint?: string;
  children: ReactNode;
}): JSX.Element => (
  <div className="flex flex-col gap-1">
    <label htmlFor={htmlFor} className="text-sm font-medium text-ink-100">
      {label}
    </label>
    {children}
    {hint !== undefined && error === undefined && <p className="text-xs text-ink-300">{hint}</p>}
    {error !== undefined && (
      <p className="text-xs text-red-300" role="alert">
        {error}
      </p>
    )}
  </div>
);

export const inputClass =
  'w-full rounded-lg border border-ink-600 bg-ink-800 px-3 py-2 text-sm text-ink-100 outline-none placeholder:text-ink-400 focus:border-brand-500';
